import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm the Laronta AI Assistant. Ask me about our tax services, booking an appointment, or what documents you'll need." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(localStorage.getItem("lpts_ai_session") || `web_${Math.random().toString(36).slice(2, 14)}`);
  const bottomRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("lpts_ai_session", sessionId.current);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${BACKEND}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") break;
          try {
            const { content } = JSON.parse(payload);
            if (content) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + content };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection issue — please call (385) 424-6444 for help." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-chat-button"
        title="Chat with the Laronta AI Assistant"
        className="fixed bottom-5 left-5 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 transition-transform duration-200"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-24 left-5 z-50 w-[92vw] max-w-sm bg-card border border-border rounded-md shadow-xl flex flex-col overflow-hidden animate-fade-up"
          style={{ height: "min(560px, 70vh)" }}
        >
          <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <div>
              <p className="text-sm font-medium">Laronta AI Assistant</p>
              <p className="text-[10px] opacity-70">Powered by Claude</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div key={i} data-testid={`ai-message-${i}`}
                className={`text-sm rounded-md px-3 py-2 max-w-[85%] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-primary text-primary-foreground ml-auto" : "bg-muted text-foreground"
                }`}>
                {m.content || (streaming && i === messages.length - 1 ? <span className="inline-block w-2 h-4 bg-muted-foreground/50 animate-pulse" /> : "")}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-border p-3 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about our services…"
              data-testid="ai-chat-input"
              className="flex-1 text-sm bg-background border border-input rounded-md px-3 py-2 outline-none focus:ring-1 focus:ring-ring"
            />
            <Button size="icon" onClick={send} disabled={streaming || !input.trim()} data-testid="ai-chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
